import { Navigate, Route, Routes } from 'react-router-dom'

import { AppShell } from './layout/AppShell.tsx'
import { PublicShell } from './layout/PublicShell.tsx'
import { RedirectIfSignedIn, RequireAuth } from './lib/session.tsx'
import { Bankroll } from './pages/Bankroll.tsx'
import { DecisionLog } from './pages/DecisionLog.tsx'
import { Home } from './pages/Home.tsx'
import { KillSwitch } from './pages/KillSwitch.tsx'
import { Leaderboard } from './pages/Leaderboard.tsx'
import { MarketDetail } from './pages/MarketDetail.tsx'
import { Markets } from './pages/Markets.tsx'
import { NotBuilt } from './pages/NotBuilt.tsx'
import { BetSlip } from './pages/BetSlip.tsx'
import { Game } from './pages/Game.tsx'
import { Player } from './pages/Player.tsx'
import { Pnl } from './pages/Pnl.tsx'
import { Positions } from './pages/Positions.tsx'
import { RiskCentre } from './pages/RiskCentre.tsx'
import { Settings } from './pages/Settings.tsx'
import { GoogleSignup } from './pages/auth/GoogleSignup.tsx'
import { Login } from './pages/auth/Login.tsx'
import { Recover } from './pages/auth/Recover.tsx'
import { Register } from './pages/auth/Register.tsx'
import { ResetPassword } from './pages/auth/ResetPassword.tsx'
import { AccountsGate, Waitlist, WaitlistGate } from './pages/auth/Waitlist.tsx'

/**
 * Two frames, and the split is the security boundary as much as a visual one.
 *
 * `PublicShell` has no sidebar, no mode pill and no kill switch, because none of those
 * mean anything without an account. `AppShell` sits behind `RequireAuth`, and the API
 * behind it refuses unauthenticated requests independently — the guard here is for the
 * user's benefit, not the server's.
 */
export function App() {
  return (
    <Routes>
      {/* ---- public ---- */}
      <Route element={<PublicShell />}>
        {/* Accounts are closed until KELLYSTAKE_ACCOUNTS_OPEN=1 on the server. Until then the
            waitlist is the way in, at its own address, and the marketing site links here. */}
        <Route
          path="/waitlist"
          element={
            <RedirectIfSignedIn>
              <WaitlistGate>
                <Waitlist />
              </WaitlistGate>
            </RedirectIfSignedIn>
          }
        />
        {/* Sign-in stays reachable while accounts are closed — nothing links to it, and the
            server admits only accounts granted early access — so it is not behind the gate.
            The screen itself says so. Sign-up is: `AccountsGate` sends it to /waitlist. */}
        <Route
          path="/login"
          element={
            <RedirectIfSignedIn>
              <Login />
            </RedirectIfSignedIn>
          }
        />
        <Route
          path="/signup"
          element={
            <RedirectIfSignedIn>
              <AccountsGate>
                <Register />
              </AccountsGate>
            </RedirectIfSignedIn>
          }
        />
        {/* Where a first-time Google sign-in lands to give the three consents. Guarded the
            same way as /signup: there is nothing here for someone who already has a session. */}
        <Route
          path="/signup/google"
          element={
            <RedirectIfSignedIn>
              <AccountsGate>
                <GoogleSignup />
              </AccountsGate>
            </RedirectIfSignedIn>
          }
        />
        {/* The page was /register until the marketing site started linking to /signup.
            Kept so older links, bookmarks and anything already printed still land. */}
        <Route path="/register" element={<Navigate to="/signup" replace />} />
        <Route path="/recover" element={<Recover />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/legal" element={<NotBuilt title="Legal" design="legal.html" publicPage />} />
        <Route path="/pricing" element={<NotBuilt title="Pricing" design="pricing.html" publicPage />} />
        <Route
          path="/how-it-works"
          element={<NotBuilt title="How it works" design="how-it-works.html" publicPage />}
        />
      </Route>

      {/* ---- the terminal ---- */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* The six core screens */}
        <Route path="/" element={<Home />} />
        <Route path="/positions" element={<Positions />} />
        <Route path="/pnl" element={<Pnl />} />
        <Route path="/bankroll" element={<Bankroll />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/players/:playerId" element={<Player />} />
        <Route path="/games/:eventId" element={<Game />} />
        {/* Listed before the detail route only for readability — the paths do not overlap. */}
        <Route path="/markets" element={<Markets />} />
        <Route path="/markets/:marketId" element={<MarketDetail />} />
        <Route path="/decision-log" element={<DecisionLog />} />
        <Route path="/risk-centre" element={<RiskCentre />} />
        <Route path="/bet-slip" element={<BetSlip />} />
        <Route path="/place-bet" element={<Navigate to="/bet-slip" replace />} />
        <Route path="/order-ticket" element={<Navigate to="/bet-slip" replace />} />

        {/* Always reachable in ≤2 interactions from anywhere */}
        <Route path="/kill-switch" element={<KillSwitch />} />

        {/* In the sitemap, designed, not yet implemented */}
        <Route path="/order-blotter" element={<NotBuilt title="Order blotter" design="order-blotter.html" />} />
        <Route path="/strategies" element={<NotBuilt title="Strategies" design="strategies.html" />} />
        <Route path="/strategies/:key" element={<NotBuilt title="Strategy detail" design="strategy-detail.html" />} />
        <Route path="/backtests" element={<NotBuilt title="Backtests" design="backtests.html" />} />
        <Route path="/performance" element={<NotBuilt title="Performance" design="performance.html" />} />
        <Route path="/system-health" element={<NotBuilt title="System health" design="system-health.html" />} />
        <Route path="/reconciliation" element={<NotBuilt title="Reconciliation" design="reconciliation.html" />} />
        <Route path="/settings/profile" element={<Settings />} />
        <Route path="/settings/security" element={<NotBuilt title="Security" design="settings-security.html" />} />
        <Route path="/onboarding" element={<NotBuilt title="Onboarding" design="onboarding.html" />} />
        <Route path="/profile" element={<Settings />} />
        <Route path="/admin/users" element={<NotBuilt title="Admin" design="admin-users.html" />} />
        <Route path="/go-live" element={<NotBuilt title="Go live" design="go-live.html" />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
